import { useEffect, useRef, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import type { RedemptionRecord } from '@shared/api.interface';
import { getRedemptions } from '@/api/member';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDateTime } from './record-format';

const PAGE_SIZE = 20;
type ResultFilter = 'all' | RedemptionRecord['result'];
const FILTERS: Array<[ResultFilter, string]> = [
  ['all', '全部'],
  ['success', '成功'],
  ['failed', '失败'],
  ['adjusted', '管理员调整'],
];
const pillClass: Record<RedemptionRecord['result'], string> = {
  success: 'bg-emerald-50 text-emerald-800',
  failed: 'bg-red-50 text-red-700',
  adjusted: 'bg-slate-100 text-slate-700',
};
const resultLabel: Record<RedemptionRecord['result'], string> = {
  success: '成功',
  failed: '失败',
  adjusted: '管理员调整',
};
const cardClass = 'rounded-2xl bg-white shadow-[0_0_0_1px_var(--border)]';
const gridClass =
  'grid grid-cols-[150px_minmax(120px,1fr)_minmax(0,1.2fr)_minmax(0,0.9fr)_96px_minmax(0,1.4fr)] items-center gap-3 px-5';

function ResultPill({ result }: { result: RedemptionRecord['result'] }) {
  return (
    <span
      className={
        'inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ' +
        pillClass[result]
      }
    >
      {resultLabel[result]}
    </span>
  );
}

function note(record: RedemptionRecord) {
  if (record.record_type === 'quota_adjustment')
    return record.adjustment_note || '未填写调整说明';
  return record.fail_reason || '';
}

export function RedemptionRecordsPanel({
  onOpenMember,
}: {
  onOpenMember?: (passcode: string) => void;
}) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [result, setResult] = useState<ResultFilter>('all');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<RedemptionRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const latest = useRef(0);

  // 输入停顿 300ms 再搜索，并回到第一页
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const id = ++latest.current;
    setLoading(true);
    setError('');
    getRedemptions({
      search: search || undefined,
      result: result === 'all' ? undefined : result,
      page,
      pageSize: PAGE_SIZE,
    })
      .then((resp) => {
        if (id !== latest.current) return;
        setItems(resp.items);
        setTotal(resp.total);
      })
      .catch(() => {
        if (id === latest.current) setError('领取记录暂时无法读取，请重试');
      })
      .finally(() => {
        if (id === latest.current) setLoading(false);
      });
  }, [search, result, page, reload]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const code = (passcode: string) =>
    onOpenMember ? (
      <button
        type="button"
        onClick={() => onOpenMember(passcode)}
        aria-label={`查看会员 ${passcode} 详情`}
        className="max-w-full justify-self-start truncate rounded font-mono text-sm tracking-wide text-foreground underline decoration-orange-200 underline-offset-4 hover:text-cta"
      >
        {passcode}
      </button>
    ) : (
      <span className="font-mono text-sm tracking-wide">{passcode}</span>
    );

  return (
    <div className="space-y-3.5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <label className="relative min-w-0 sm:w-[340px]">
          <Search
            aria-hidden
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label="搜兑换码"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="搜兑换码，输入一部分即可"
            className="h-11 rounded-xl bg-white pl-9"
          />
        </label>
        <div
          role="group"
          aria-label="按结果筛选"
          className="flex gap-0.5 overflow-x-auto rounded-xl bg-muted p-1"
        >
          {FILTERS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              aria-pressed={result === value}
              onClick={() => {
                setResult(value);
                setPage(1);
              }}
              className={
                'min-h-9 shrink-0 whitespace-nowrap rounded-[9px] px-3.5 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-cta ' +
                (result === value
                  ? 'bg-card font-semibold text-cta shadow-sm'
                  : 'text-accent-foreground hover:text-foreground')
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <div role="alert" className={cardClass + ' space-y-3 p-5'}>
          <p className="text-sm text-red-700">{error}</p>
          <Button variant="outline" onClick={() => setReload((n) => n + 1)}>
            重试
          </Button>
        </div>
      ) : loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div
          aria-busy={loading}
          className={loading ? 'opacity-60 transition-opacity' : ''}
        >
          {/* 电脑：表格 */}
          <div className={cardClass + ' hidden overflow-hidden lg:block'}>
            <div
              className={
                gridClass +
                ' h-[42px] bg-background text-[13px] font-medium text-muted-foreground'
              }
            >
              <span>时间</span>
              <span>兑换码</span>
              <span>主题</span>
              <span>会员类型</span>
              <span>结果</span>
              <span>说明</span>
            </div>
            {items.map((record) => {
              const text = note(record);
              return (
                <div
                  key={record.id}
                  className={
                    gridClass +
                    ' min-h-[50px] border-t border-border/60 py-1.5 text-sm'
                  }
                >
                  <span className="tabular-nums text-accent-foreground">
                    {formatDateTime(record.redemption_time)}
                  </span>
                  {code(record.passcode)}
                  <span className="truncate" title={record.topic_name}>
                    {record.record_type === 'quota_adjustment'
                      ? '—'
                      : record.topic_name}
                  </span>
                  <span className="truncate text-accent-foreground">
                    {record.record_type === 'quota_adjustment'
                      ? '—'
                      : record.member_type}
                  </span>
                  <span>
                    <ResultPill result={record.result} />
                  </span>
                  <span
                    className={
                      'truncate text-[13px] ' +
                      (record.result === 'failed'
                        ? 'text-red-700'
                        : 'text-muted-foreground')
                    }
                    title={text}
                  >
                    {text || '—'}
                  </span>
                </div>
              );
            })}
            {items.length === 0 && (
              <p className="border-t border-border/60 py-10 text-center text-sm text-muted-foreground">
                没有符合条件的记录
              </p>
            )}
          </div>

          {/* 手机：卡片 */}
          <div className="space-y-2.5 lg:hidden">
            {items.map((record) => {
              const text = note(record);
              return (
                <article
                  key={record.id}
                  className={cardClass + ' space-y-1.5 px-3.5 py-3'}
                >
                  <div className="flex items-center justify-between gap-2">
                    {code(record.passcode)}
                    <ResultPill result={record.result} />
                  </div>
                  {record.record_type !== 'quota_adjustment' && (
                    <p className="break-words text-[15px] font-medium">
                      {record.topic_name}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(record.redemption_time)}
                    {record.record_type !== 'quota_adjustment' &&
                      ` · ${record.member_type}`}
                  </p>
                  {text && (
                    <p
                      className={
                        'break-words text-[13px] ' +
                        (record.result === 'failed'
                          ? 'text-red-700'
                          : 'text-muted-foreground')
                      }
                    >
                      {text}
                    </p>
                  )}
                </article>
              );
            })}
            {items.length === 0 && (
              <p
                className={
                  cardClass + ' py-8 text-center text-sm text-muted-foreground'
                }
              >
                没有符合条件的记录
              </p>
            )}
          </div>
        </div>
      )}

      {!error && (
        <div
          aria-label="领取记录分页"
          className="flex flex-wrap items-center justify-between gap-3 text-sm"
        >
          <span aria-live="polite" className="text-accent-foreground">
            共 {total} 条 · 第 {page}/{totalPages} 页
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={loading || page <= 1}
              onClick={() => setPage(page - 1)}
            >
              上一页
            </Button>
            <Button
              variant="outline"
              disabled={loading || page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              下一页
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
